import React, { useMemo } from 'react';
import { Button, ButtonGroup, Table, Tag } from 'rsuite';
import PermissionGate from '../shared/PermissionGate';
import TimeOffStatusBadge from './TimeOffStatusBadge';
import { useAuth } from '../../contexts/AuthContext';
import './TimeOffList.css';

const { Column, HeaderCell, Cell } = Table;

export default function TimeOffList({
  requests,
  onView,
  onReview,
  onApprove,
  onReject,
  onCancel,
  showEmployeeInfo = false,
  showEmployeeEmail = false,
  showRequestType = true,
  showCreatedOn = false,
  createdOnColumnWidth = 160,
  actionsColumnWidth = 240,
  tableClassName = '',
  combineDateColumns = false,
  combinedDatesColumnWidth = 260,
  notesColumnMinWidth = 200,
  notesColumnFlexGrow = 1,
  startEndDisplay = 'badges',
  showCancelAction = true,
}) {
  const auth = useAuth();
  const data = useMemo(() => requests || [], [requests]);
  const currentEmployeeId = auth.employee?.id;
  const isRowClickable = typeof onView === 'function';
  const resolvedTableClassName = [tableClassName, isRowClickable ? 'time-off-list--row-clickable' : '']
    .filter(Boolean)
    .join(' ');

  const parseLocalDateOnly = (value) => {
    if (!value) return null;
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return new Date(value.getFullYear(), value.getMonth(), value.getDate());
    }
    const s = String(value).trim();
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return null;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  };

  const formatMonthDay = (value) => {
    const d = parseLocalDateOnly(value);
    if (!d) return String(value ?? '');
    const month = d.toLocaleString('en-US', { month: 'short' });
    const day = String(d.getDate()).padStart(2, '0');
    return `${month} ${day}`;
  };

  const formatLongDate = (value) => {
    const d = parseLocalDateOnly(value);
    if (!d) return String(value ?? '');
    return d.toLocaleDateString('en-US', {
      month: 'long',
      day: '2-digit',
      year: 'numeric',
    });
  };

  const formatDateRange = (startValue, endValue) => {
    const start = parseLocalDateOnly(startValue);
    const end = parseLocalDateOnly(endValue);
    if (!start && !end) return '';
    if (!start) return formatLongDate(end);
    if (!end) return formatLongDate(start);

    const sameDay = start.getTime() === end.getTime();
    if (sameDay) {
      return formatLongDate(start);
    }

    const sameYear = start.getFullYear() === end.getFullYear();
    const startMonth = start.toLocaleDateString('en-US', { month: 'long' });
    const endMonth = end.toLocaleDateString('en-US', { month: 'long' });
    const startDay = String(start.getDate()).padStart(2, '0');
    const endDay = String(end.getDate()).padStart(2, '0');

    if (sameYear) {
      if (startMonth === endMonth) {
        return `${startMonth} ${startDay} to ${endDay}, ${start.getFullYear()}`;
      }
      return `${startMonth} ${startDay} to ${endMonth} ${endDay}, ${start.getFullYear()}`;
    }

    return `${formatLongDate(start)} to ${formatLongDate(end)}`;
  };

  const formatYmdWithTime = (value) => {
    if (!value) return '';
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return String(value ?? '');

    const formatted = d.toLocaleString('en-US', {
      month: 'long',
      day: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

    return formatted.replace(/\s([AP]M)$/, (_full, meridiem) => meridiem.toLowerCase());
  };

  const formatTimeFromMinutes = (minutes) => {
    const m = Number(minutes);
    if (!Number.isFinite(m)) return '';
    const h24 = Math.floor(m / 60) % 24;
    const mins = m % 60;
    const ampm = h24 >= 12 ? 'pm' : 'am';
    let h12 = h24 % 12;
    if (h12 === 0) h12 = 12;
    return mins === 0 ? `${h12}${ampm}` : `${h12}:${String(mins).padStart(2, '0')}${ampm}`;
  };

  const formatBadgeText = (dateValue, minutesValue) => {
    const base = formatMonthDay(dateValue);
    if (minutesValue == null) return { dateText: base, timeText: null };
    return { dateText: base, timeText: formatTimeFromMinutes(minutesValue) };
  };

  return (
    <Table
      autoHeight
      data={data}
      bordered
      cellBordered
      hover
      className={resolvedTableClassName}
      onRowClick={(rowData) => onView?.(rowData)}
    >
      {showEmployeeInfo ? (
        <>
          <Column width={170}>
            <HeaderCell>Employee</HeaderCell>
            <Cell>
              {(rowData) => {
                const first = rowData.first_name || rowData.employee_first_name || '';
                const last = rowData.last_name || rowData.employee_last_name || '';
                const name = `${first} ${last}`.trim();
                return name || '';
              }}
            </Cell>
          </Column>

          {showEmployeeEmail ? (
            <Column width={220}>
              <HeaderCell>Email</HeaderCell>
              <Cell>
                {(rowData) => rowData.email || rowData.employee_email || ''}
              </Cell>
            </Column>
          ) : null}
        </>
      ) : null}

      {showRequestType ? (
        <Column width={140}>
          <HeaderCell>Type</HeaderCell>
          <Cell>
            {(rowData) => <span style={{ textTransform: 'capitalize' }}>{rowData.request_type}</span>}
          </Cell>
        </Column>
      ) : null}

      {combineDateColumns ? (
        <Column width={combinedDatesColumnWidth}>
          <HeaderCell>Dates</HeaderCell>
          <Cell>
            {(rowData) => formatDateRange(rowData.start_date, rowData.end_date)}
          </Cell>
        </Column>
      ) : (
        <>
          <Column width={190}>
            <HeaderCell>Start</HeaderCell>
            <Cell>
              {(rowData) => {
                if (startEndDisplay === 'datetime') {
                  const dateText = formatLongDate(rowData.start_date);
                  const timeText = rowData.start_minutes == null ? null : formatTimeFromMinutes(rowData.start_minutes);
                  return <span>{timeText ? `${dateText} ${timeText}` : dateText}</span>;
                }

                const v = formatBadgeText(rowData.start_date, rowData.start_minutes);
                return (
                  <div>
                    <Tag size="sm" appearance="ghost">{v.dateText}</Tag>
                    <div className="text-muted" style={{ fontSize: 12, marginTop: 4, minHeight: 16 }}>
                      Start{v.timeText ? ` @ ${v.timeText}` : ''}
                    </div>
                  </div>
                );
              }}
            </Cell>
          </Column>

          <Column width={190}>
            <HeaderCell>End</HeaderCell>
            <Cell>
              {(rowData) => {
                if (startEndDisplay === 'datetime') {
                  const dateText = formatLongDate(rowData.end_date);
                  const timeText = rowData.end_minutes == null ? null : formatTimeFromMinutes(rowData.end_minutes);
                  return <span>{timeText ? `${dateText} ${timeText}` : dateText}</span>;
                }

                const v = formatBadgeText(rowData.end_date, rowData.end_minutes);
                return (
                  <div>
                    <Tag size="sm" appearance="ghost">{v.dateText}</Tag>
                    <div className="text-muted" style={{ fontSize: 12, marginTop: 4, minHeight: 16 }}>
                      End{v.timeText ? ` @ ${v.timeText}` : ''}
                    </div>
                  </div>
                );
              }}
            </Cell>
          </Column>
        </>
      )}

      <Column width={110}>
        <HeaderCell>Hours</HeaderCell>
        <Cell>
          {(rowData) => (rowData.total_hours != null ? Number(rowData.total_hours) : '')}
        </Cell>
      </Column>

      <Column width={140}>
        <HeaderCell>Status</HeaderCell>
        <Cell>
          {(rowData) => <TimeOffStatusBadge status={rowData.status} approvalReason={rowData.approval_reason} />}
        </Cell>
      </Column>

      {showCreatedOn ? (
        <Column width={createdOnColumnWidth}>
          <HeaderCell>Created On</HeaderCell>
          <Cell>
            {(rowData) => formatYmdWithTime(rowData.created_at)}
          </Cell>
        </Column>
      ) : null}

      <Column flexGrow={notesColumnFlexGrow} minWidth={notesColumnMinWidth}>
        <HeaderCell>Notes</HeaderCell>
        <Cell dataKey="notes" />
      </Column>

      <Column width={actionsColumnWidth} fixed="right">
        <HeaderCell>Actions</HeaderCell>
        <Cell>
          {(rowData) => (
            <ButtonGroup size="xs">
              <PermissionGate anyOf={['approve_time_off']} fallback={null}>
                {onReview ? (
                  <Button
                    appearance="primary"
                    onClick={(e) => { e.stopPropagation(); onReview?.(rowData); }}
                    disabled={String(rowData.status).toLowerCase() !== 'pending'}
                  >
                    Review
                  </Button>
                ) : (
                  <>
                    <Button
                      appearance="primary"
                      onClick={(e) => { e.stopPropagation(); onApprove?.(rowData); }}
                      disabled={String(rowData.status).toLowerCase() !== 'pending'}
                    >
                      Approve
                    </Button>
                    <Button
                      color="red"
                      appearance="ghost"
                      onClick={(e) => { e.stopPropagation(); onReject?.(rowData); }}
                      disabled={String(rowData.status).toLowerCase() !== 'pending'}
                    >
                      Reject
                    </Button>
                  </>
                )}
              </PermissionGate>

              {showCancelAction && currentEmployeeId && rowData.employee_id === currentEmployeeId ? (
                <Button
                  appearance="ghost"
                  onClick={(e) => { e.stopPropagation(); onCancel?.(rowData); }}
                  disabled={String(rowData.status).toLowerCase() !== 'pending'}
                >
                  Cancel
                </Button>
              ) : null}
            </ButtonGroup>
          )}
        </Cell>
      </Column>
    </Table>
  );
}
